import { useState } from "react";
import { ROOMS, OBJECTS, FLOOR, deptsInRoom, type Room, type OfficeObject } from "../data/office";
import { Cafeteria } from "./Cafeteria";
import { ALL_DEPTS, PERSONAS, type PersonaDef } from "../data/org";
import { useRH } from "../store/rh";
import { useUI } from "../store/ui";
import { initials, monogramColor } from "../lib/monogram";
import { profileOf } from "../data/profiles";

const SCALE = 16; // px por unidade
const W = FLOOR.w * SCALE;
const H = FLOOR.h * SCALE;

const ROOM_TONE: Record<Room["kind"], string> = {
  council:    "#BC2A24",  // vermelho
  management: "#121214",  // preto
  meeting:    "#A8863C",  // ouro
  dept:       "#F2EEE6",
  common:     "#EDE7D9"
};

const ROOM_FG: Record<Room["kind"], string> = {
  council:    "#F2EEE6",
  management: "#F2EEE6",
  meeting:    "#121214",
  dept:       "#121214",
  common:     "#3C3A36"
};

export function OfficeFloor() {
  const employees = useRH(s => s.employees);
  const toggleM   = useUI(s => s.toggleMeeting);
  const meetPicks = useUI(s => s.meetingPicks);
  const setView   = useUI(s => s.setView);

  const [hover, setHover] = useState<{ x: number; y: number; persona: PersonaDef } | null>(null);
  const [hoverObj, setHoverObj] = useState<OfficeObject | null>(null);
  const [cafeOpen, setCafeOpen] = useState(false);

  // contratados por sala
  const hiredByRoom: Record<string, PersonaDef[]> = {};
  ROOMS.forEach(r => {
    const dpts = deptsInRoom(r);
    const list: PersonaDef[] = [];
    PERSONAS.forEach(p => {
      const emp = employees[p.id];
      if (!emp || emp.status !== "hired") return;
      const [deptId] = p.slotId.split(":");
      if (dpts.includes(deptId)) list.push(p);
    });
    hiredByRoom[r.id] = list;
  });

  return (
    <div className="px-8 pb-16">
      <header className="border-2 border-ink bg-ink text-bone px-5 py-4 mb-4 flex items-end justify-between">
        <div>
          <div className="font-mono text-xs text-gold">PLANTA-01</div>
          <h2 className="font-display font-black uppercase text-4xl leading-none">ESCRITÓRIO VPC</h2>
          <div className="font-serif italic mt-1 text-bone/70">
            planta-baixa · 1 andar · {Object.values(hiredByRoom).flat().length} agentes em campo
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Legend color="#BC2A24"  label="conselho" />
          <Legend color="#121214"  label="gerência" />
          <Legend color="#A8863C"  label="sala de reunião" />
          <Legend color="#F2EEE6"  label="departamento" border />
          <Legend color="#EDE7D9"  label="área comum"   border />
        </div>
      </header>

      <div className="border-2 border-ink bg-bone overflow-auto">
        <div className="relative" style={{ width: W, height: H }}>
          <svg width={W} height={H} className="absolute inset-0">
            {/* grid sutil */}
            <defs>
              <pattern id="g" width={SCALE} height={SCALE} patternUnits="userSpaceOnUse">
                <path d={`M ${SCALE} 0 L 0 0 0 ${SCALE}`} fill="none" stroke="#121214" strokeOpacity="0.06" strokeWidth="1" />
              </pattern>
            </defs>
            <rect width={W} height={H} fill="url(#g)" />

            {/* salas */}
            {ROOMS.map(r => (
              <g key={r.id}>
                <rect
                  x={r.x * SCALE} y={r.y * SCALE}
                  width={r.w * SCALE} height={r.h * SCALE}
                  fill={ROOM_TONE[r.kind]}
                  stroke="#121214"
                  strokeWidth={2}
                />
                {/* header band */}
                <rect
                  x={r.x * SCALE} y={r.y * SCALE}
                  width={r.w * SCALE} height={20}
                  fill="#121214"
                />
                <text
                  x={r.x * SCALE + 8} y={r.y * SCALE + 14}
                  fontFamily="JetBrains Mono" fontSize="10" fill="#A8863C">
                  {r.code}
                </text>
                <text
                  x={(r.x + r.w) * SCALE - 8} y={r.y * SCALE + 14}
                  fontFamily="JetBrains Mono" fontSize="10" fill="#F2EEE6"
                  textAnchor="end">
                  {hiredByRoom[r.id]?.length ?? 0} agente{(hiredByRoom[r.id]?.length ?? 0) === 1 ? "" : "s"}
                </text>
                {/* room name */}
                <text
                  x={r.x * SCALE + 10} y={r.y * SCALE + 40}
                  fontFamily="Big Shoulders Display" fontWeight="900" fontSize="20"
                  fill={ROOM_FG[r.kind]}>
                  {r.icon ? `${r.icon}  ` : ""}{r.name.toUpperCase()}
                </text>
                {/* dept label if dept */}
                {r.deptId && (
                  <text
                    x={r.x * SCALE + 10} y={r.y * SCALE + 56}
                    fontFamily="Crimson Pro" fontSize="11" fontStyle="italic"
                    fill={ROOM_FG[r.kind]} opacity={0.75}>
                    {deptsInRoom(r).map(d => ALL_DEPTS.find(x => x.id === d)?.name ?? d).join(" · ").toLowerCase()}
                  </text>
                )}
              </g>
            ))}

            {/* mesas dos agentes — distribuídas dentro de cada sala */}
            {ROOMS.map(r => {
              const ppl = hiredByRoom[r.id] ?? [];
              if (ppl.length === 0) return null;
              // grid de mesas dentro da sala (abaixo do header band)
              const padX = 8;
              const padTop = 64;
              const padBot = 8;
              const innerW = r.w * SCALE - padX * 2;
              const innerH = r.h * SCALE - padTop - padBot;
              const cols = Math.max(1, Math.floor(innerW / 70));
              const rows = Math.ceil(ppl.length / cols);
              const cellW = innerW / cols;
              const cellH = Math.min(60, innerH / rows);

              return ppl.map((p, idx) => {
                const col = idx % cols;
                const row = Math.floor(idx / cols);
                const cx = r.x * SCALE + padX + col * cellW + cellW / 2;
                const cy = r.y * SCALE + padTop + row * cellH + cellH / 2;
                const isPicked = meetPicks.includes(p.id);
                const av = employees[p.id]?.avatarUrl;
                const clipId = `clip-${p.id}`;
                return (
                  <g
                    key={p.id}
                    style={{ cursor: "pointer" }}
                    onMouseEnter={() => setHover({ x: cx, y: cy, persona: p })}
                    onMouseLeave={() => setHover(null)}
                    onClick={() => toggleM(p.id)}
                  >
                    <rect x={cx - 26} y={cy - 6} width={52} height={14} fill="#3C3A36" />
                    <rect x={cx - 8}  y={cy - 14} width={16} height={8} fill="#121214" />
                    {av ? (
                      <>
                        <defs>
                          <clipPath id={clipId}>
                            <circle cx={cx} cy={cy + 18} r={13} />
                          </clipPath>
                        </defs>
                        <image
                          href={av}
                          x={cx - 13} y={cy + 5} width={26} height={26}
                          preserveAspectRatio="xMidYMid slice"
                          clipPath={`url(#${clipId})`}
                        />
                        <circle cx={cx} cy={cy + 18} r={13}
                                fill="none"
                                stroke={isPicked ? "#A8863C" : "#121214"}
                                strokeWidth={isPicked ? 3 : 2} />
                      </>
                    ) : (
                      <>
                        <circle cx={cx} cy={cy + 18} r={13}
                                fill={monogramColor(p.id)}
                                stroke={isPicked ? "#A8863C" : "#121214"}
                                strokeWidth={isPicked ? 3 : 2} />
                        <text x={cx} y={cy + 22}
                              textAnchor="middle"
                              fontFamily="Big Shoulders Display" fontWeight="900" fontSize="11"
                              fill="#F2EEE6">
                          {initials(p.name)}
                        </text>
                      </>
                    )}
                  </g>
                );
              });
            })}

            {/* objetos do escritório (cafeteria, impressora, etc) */}
            {OBJECTS.map(o => (
              <g
                key={o.id}
                style={{ cursor: o.kind === "coffee" ? "pointer" : "help" }}
                onMouseEnter={() => setHoverObj(o)}
                onMouseLeave={() => setHoverObj(null)}
                onClick={() => { if (o.kind === "coffee") setCafeOpen(true); }}
              >
                <rect
                  x={o.x * SCALE - 14} y={o.y * SCALE - 14}
                  width={28} height={28}
                  fill="#F2EEE6" stroke="#121214" strokeWidth={2}
                />
                <text x={o.x * SCALE} y={o.y * SCALE + 6}
                      textAnchor="middle"
                      fontFamily="serif" fontSize="18" fill="#121214">
                  {o.icon}
                </text>
                <text x={o.x * SCALE} y={o.y * SCALE + 24}
                      textAnchor="middle"
                      fontFamily="JetBrains Mono" fontSize="9" fill="#3C3A36">
                  {o.label.toLowerCase()}
                </text>
              </g>
            ))}

            {/* tooltip persona */}
            {hover && (
              <g style={{ pointerEvents: "none" }}>
                <rect
                  x={Math.min(hover.x + 20, W - 280)}
                  y={Math.max(hover.y - 60, 0)}
                  width={280} height={70}
                  fill="#121214" stroke="#A8863C" strokeWidth={2}
                />
                <text
                  x={Math.min(hover.x + 30, W - 270)}
                  y={Math.max(hover.y - 40, 20)}
                  fontFamily="Big Shoulders Display" fontWeight="900" fontSize="14"
                  fill="#F2EEE6">
                  {hover.persona.name.toUpperCase()}
                </text>
                <text
                  x={Math.min(hover.x + 30, W - 270)}
                  y={Math.max(hover.y - 24, 36)}
                  fontFamily="Crimson Pro" fontSize="11" fontStyle="italic"
                  fill="#F2EEE6">
                  {hover.persona.bio.slice(0, 50)}{hover.persona.bio.length > 50 ? "…" : ""}
                </text>
                <text
                  x={Math.min(hover.x + 30, W - 270)}
                  y={Math.max(hover.y - 8, 52)}
                  fontFamily="JetBrains Mono" fontSize="9" fill="#A8863C">
                  clique para convocar à reunião
                </text>
              </g>
            )}

            {/* tooltip objeto */}
            {hoverObj && (
              <g style={{ pointerEvents: "none" }}>
                <rect
                  x={hoverObj.x * SCALE + 20}
                  y={hoverObj.y * SCALE - 24}
                  width={260} height={36}
                  fill="#F2EEE6" stroke="#121214" strokeWidth={2}
                />
                <text
                  x={hoverObj.x * SCALE + 30}
                  y={hoverObj.y * SCALE - 8}
                  fontFamily="Big Shoulders Display" fontWeight="900" fontSize="12"
                  fill="#121214">
                  {hoverObj.label.toUpperCase()}
                </text>
                {hoverObj.hint && (
                  <text
                    x={hoverObj.x * SCALE + 30}
                    y={hoverObj.y * SCALE + 8}
                    fontFamily="Crimson Pro" fontSize="10" fontStyle="italic"
                    fill="#3C3A36">
                    {hoverObj.hint}
                  </text>
                )}
              </g>
            )}
          </svg>
        </div>
      </div>

      <div className="mt-4 flex justify-between items-center">
        <p className="font-crimson italic text-paper text-sm">
          dica: clique numa mesa pra convocar pra reunião · clique na ☕ pra abrir a cafeteria · {meetPicks.length} convocados
        </p>
        <div className="flex gap-2">
          <button onClick={() => setCafeOpen(true)} className="btn bg-gold text-ink border-gold">
            ☕ cafeteria
          </button>
          <button onClick={() => setView("meeting")} className="btn bg-ink text-bone border-ink">
            ir à sala de reunião →
          </button>
        </div>
      </div>

      {cafeOpen && <Cafeteria onClose={() => setCafeOpen(false)} />}
    </div>
  );
}

function Legend({ color, label, border }: { color: string; label: string; border?: boolean }) {
  return (
    <span className="flex items-center gap-1.5 tag">
      <span className="inline-block w-4 h-4"
            style={{ background: color, border: border ? "2px solid #121214" : "none" }} />
      {label}
    </span>
  );
}
